import { formatCnpj } from '@/lib/document';
import type { TransactionEstablishment } from '@/types';

export interface MerchantDetailSource {
  merchantName?: string | null;
  merchantCnpj?: string | null;
  merchantCategory?: string | null;
  merchantMcc?: number | null;
  merchantCnae?: string | null;
}

export interface MerchantDetailDisplay {
  name: string;
  metadata: string[];
  /** True when the row is showing the curated identity rather than Pluggy's strings —
   * the only case where the CNPJ is registry-validated and worth linking out. */
  curated: boolean;
}

function firstPresent(...values: Array<string | number | null | undefined>): string | undefined {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return undefined;
}

export function buildMerchantDetailDisplay(
  details?: MerchantDetailSource | null,
  source?: MerchantDetailSource | null,
  establishment?: TransactionEstablishment | null,
): MerchantDetailDisplay | null {
  // The curated identity wins outright when there is one: its razão social and CNPJ come
  // from the registry, where Pluggy's merchantName is whatever the acquirer typed. The
  // address is NOT folded in here — it belongs to the company, not to this purchase.
  if (establishment) {
    const metadata = [
      establishment.legalName,
      establishment.cnpj ? formatCnpj(establishment.cnpj) : null,
      establishment.serviceType,
      establishment.cnae ? `CNAE ${establishment.cnae}` : null,
    ].filter((item): item is string => !!item);
    return { name: establishment.brandName, metadata, curated: true };
  }

  const name = firstPresent(details?.merchantName, source?.merchantName);
  if (!name) return null;

  const cnpj = firstPresent(details?.merchantCnpj, source?.merchantCnpj);
  const category = firstPresent(details?.merchantCategory, source?.merchantCategory);
  const mcc = firstPresent(details?.merchantMcc, source?.merchantMcc);
  const cnae = firstPresent(details?.merchantCnae, source?.merchantCnae);
  const metadata = [
    cnpj ? formatCnpj(cnpj) : null,
    category,
    mcc ? `MCC ${mcc}` : null,
    cnae ? `CNAE ${cnae}` : null,
  ].filter((item): item is string => !!item);

  return { name, metadata, curated: false };
}
